import React, { useState } from 'react';
import {
    Card,
    Tabs,
    Button,
    Space,
    Typography,
    Input,
    DatePicker,
    Select,
    Form
} from 'antd';
import {
    ReloadOutlined,
    FilterOutlined,
    SearchOutlined
} from '@ant-design/icons';
import RequestList from '../components/requests/RequestList';
import { ActionFlowRequestSearch } from "@/shared/api/model";
import { useQuery } from '@tanstack/react-query';
import { getMyRequests } from "@/shared/api/client/action-flow";
import dayjs from 'dayjs';
import createParamsSerializer from '@/shared/utils/create-params-serializer';

const { Title } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

export const MyRequestsView: React.FC = () => {
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState('pending');
    const [searchParams, setSearchParams] = useState<Omit<ActionFlowRequestSearch, 'status'>>({
        numberOfRows: 40,
        sortBy: 'scopeDate',
        orderBy: 'desc'
    });

    // Fetch requests belonging to the current user with selected status
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['myRequests', searchParams, activeTab],
        queryFn: async () => {
            const response = await getMyRequests(
                {
                    ...searchParams,
                    // @ts-ignore
                    status: activeTab.toUpperCase()
                },
                {
                    paramsSerializer: createParamsSerializer()
                }
            );
            return response.data;
        }
    });

    const requests = Array.isArray(data) ? data : [];

    const handleSearch = (values: any) => {
        const { dateRange, ...rest } = values;
        
        const newParams: Omit<ActionFlowRequestSearch, 'status'> = {
            ...searchParams,
            ...rest,
            from: dateRange ? dayjs(dateRange[0]).startOf('day').toDate() : undefined,
            to: dateRange ? dayjs(dateRange[1]).endOf('day').toDate() : undefined
        };
        
        setSearchParams(newParams);
    };

    const handleTabChange = (key: string) => {
        setActiveTab(key);
    };

    const handleReset = () => {
        form.resetFields();
        setSearchParams({
            numberOfRows: 40,
            sortBy: 'scopeDate',
            orderBy: 'desc'
        });
    };

    const renderSearchForm = () => (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSearch}
            className="mb-4"
        >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Form.Item name="q" label="Search">
                    <Input
                        prefix={<SearchOutlined />}
                        placeholder="Search records..."
                        allowClear
                    />
                </Form.Item>

                <Form.Item name="domain" label="Domain">
                    <Select
                        placeholder="Select domain"
                        allowClear
                        options={[
                            { label: 'Loans', value: 'LOANS' },
                            { label: 'Accounts', value: 'ACCOUNTS' },
                            { label: 'Customers', value: 'CUSTOMERS' }
                        ]}
                    />
                </Form.Item>

                <Form.Item name="dateRange" label="Date Range">
                    <RangePicker style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item label=" " className="flex justify-end">
                    <Space>
                        <Button onClick={handleReset}>
                            Reset
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<FilterOutlined />}
                        >
                            Filter
                        </Button>
                    </Space>
                </Form.Item>
            </div>
        </Form>
    );

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <Title level={2}>My Requests</Title>
                <Button
                    icon={<ReloadOutlined />}
                    onClick={() => refetch()}
                    loading={isLoading}
                >
                    Refresh
                </Button>
            </div>

            <Card>
                {renderSearchForm()}

                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    className="mb-4"
                >
                    <TabPane tab="Pending" key="pending" />
                    <TabPane tab="Approved" key="approved" />
                    <TabPane tab="Rejected" key="rejected" />
                    <TabPane tab="Returned" key="returned" />
                    <TabPane tab="Completed" key="completed" />
                </Tabs>

                <RequestList
                    requests={requests}
                    isLoading={isLoading}
                    error={error}
                    showResendAction={activeTab === 'completed' || activeTab === 'approved'}
                    // @ts-ignore
                    hideSubmissionStep={true}
                    // @ts-ignore
                    searchParams={undefined}
                    // @ts-ignore
                    onParamsChange={function (params: ActionFlowRequestSearch): void {
                        throw new Error('Function not implemented.');
                    }}
                />
            </Card>

            {/* Help Section */}
            <Card title="Help & Information" className="mt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <Title level={4}>Request Status</Title>
                        <ul className="list-disc pl-4">
                            <li>Pending: Your requests awaiting approval</li>
                            <li>Approved: Your successfully approved requests</li>
                            <li>Rejected: Your declined requests</li>
                            <li>Returned: Your requests sent back for revision</li>
                            <li>Completed: Your processed and completed requests</li>
                        </ul>
                    </div>
                    <div>
                        <Title level={4}>About My Requests</Title>
                        <ul className="list-disc pl-4">
                            <li>View all your submitted requests in one place</li>
                            <li>Filter by status using the tabs above</li>
                            <li>Track the progress of your requests</li>
                            <li>View details and history of each request</li>
                        </ul>
                    </div>
                    <div>
                        <Title level={4}>Search Tips</Title>
                        <ul className="list-disc pl-4">
                            <li>Use the search box to find specific records</li>
                            <li>Filter by date range to narrow results</li>
                            <li>Select domain to focus on specific areas</li>
                            <li>Use Reset to clear all filters</li>
                        </ul>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default MyRequestsView;
